/**
 * Required profile completion modal for SSO self-provisioned users.
 * Collects phone + last 4 ID digits. Cannot be skipped.
 * English-only (no i18n).
 */

import React, { useState } from 'react';
import { z } from 'zod';
import { Phone, CreditCard } from 'lucide-react';
import { Modal, Button } from '../../shared/components/ui';
import { FormTextField } from '../../shared/components/forms';
import { authService } from '../../services';
import { notify } from '../../shared/services';
import { useAuth } from '../../contexts/AuthContext';

const phoneRegex = /^(05\d{8}|\+?\d{8,15})$/;

const schema = z.object({
  phone: z
    .string()
    .trim()
    .min(1, 'Phone number is required')
    .regex(phoneRegex, 'Enter a valid phone number'),
  idNumberLast4: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'Enter exactly 4 digits'),
});

interface CompleteProfileModalProps {
  isOpen: boolean;
  onCompleted: () => Promise<void> | void;
}

const CompleteProfileModal: React.FC<CompleteProfileModalProps> = ({
  isOpen,
  onCompleted,
}) => {
  const { logout } = useAuth();
  const [phone, setPhone] = useState('');
  const [idNumberLast4, setIdNumberLast4] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    phone?: string;
    idNumberLast4?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = schema.safeParse({ phone, idNumberLast4 });
    if (!parsed.success) {
      const nextErrors: { phone?: string; idNumberLast4?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === 'phone' || key === 'idNumberLast4') {
          nextErrors[key] = issue.message;
        }
      }
      setFieldErrors(nextErrors);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    try {
      await authService.completeSelfProfile(parsed.data);
      notify.success('Profile updated');
      await onCompleted();
    } catch {
      notify.error('Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => undefined}
      title="Complete your profile"
      subtitle="Add your phone and the last 4 digits of your ID to continue."
      size="sm"
      showCloseButton={false}
      dismissible={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-2">
        <FormTextField
          name="phone"
          label="Phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          errorMessage={fieldErrors.phone}
          helperText="Israeli (05XXXXXXXX) or international format"
          startIcon={<Phone size={18} />}
          autoComplete="tel"
          inputMode="tel"
          disabled={isSubmitting}
        />
        <FormTextField
          name="idNumberLast4"
          label="Last 4 digits of ID"
          value={idNumberLast4}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
            setIdNumberLast4(digits);
          }}
          errorMessage={fieldErrors.idNumberLast4}
          helperText="Only the last 4 digits of your Israeli ID"
          startIcon={<CreditCard size={18} />}
          inputMode="numeric"
          autoComplete="off"
          disabled={isSubmitting}
        />
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => void logout()}
            disabled={isSubmitting}
          >
            Log out
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CompleteProfileModal;
